import Api from "../../lib/api";
import Dropdown from "../../components/base/dropdown";
import Collapsible from "../../components/base/collapsible-v2";
import LoadMoreObserver from "../../components/load-more-observer";
import { throttle } from "../../lib";

const showItems = ($items) => {
	if ($items.length) {
		$items.forEach(($item, i) => {
			$item.classList.remove('-transparent');
		});
	}
}

export class PostFilterService {
	constructor($url) {
		this.urlOrigin = $url == null ? craft.env.apiSiteUrl + '/feed/blog/posts' : $url;
		this.url = this.urlOrigin;
		this.searchValue = null;
		this.filters = [];
		this.requests = [];

		this.loadMoreModule = new LoadMoreObserver({
			$root: document.querySelector('[data-observer-elem="root"]'),
			moreUrl: this.url,
			afterObserve: () => {
				const $items = document.querySelectorAll('[data-observer-elem="item"].-transparent');
				if ($items.length) {
					showItems($items);
				}
			}
		});
	}

	getFilterValue(name) {
		const filter = this.filters.find((filter) => filter.name === name);

		if (!filter) {
			return null;
		}

		return filter.value;
	}

	setFilter(name, value) {
		const newFilter = Object.assign({}, { name, value });

		/* Find existing filter index */
		const filterIndex = this.filters.findIndex((filter) => filter.name === newFilter.name);

		/* If filter does not exist, just push it to the list */
		if (filterIndex === -1) {
			this.filters.push(newFilter);
		} else {
			/* If filter exists, replace it */
			this.filters = [
				...this.filters.slice(0, filterIndex),
				newFilter,
				...this.filters.slice(filterIndex + 1)
			];
		}
	}

	updateFilters(payload, options = {}) {
		if (!payload.name || !payload.value) {
			throw new Error('PostFilter: Filter update payload must have name and value data');
		}

		const shouldUpdateUrl = options.shouldUpdateUrl == null ? true : options.shouldUpdateUrl;

		this.setFilter(payload.name, payload.value);

		if (options.dropByName && typeof options.dropByName === 'string') {
			this.filters = this.filters.filter((filter) => filter.name !== options.dropByName);
		}

		if (shouldUpdateUrl) {
			this.refreshUrl();
		}
	}

	refreshUrl() {
		if (!this.filters.length && !this.searchValue) {
			return;
		}

		let params = {};

		if (this.filters.length) {
			params = this.filters.reduce((acc, cur) => {
				return Object.assign({}, acc, { [cur.name]: cur.value });
			}, {});
		}

		// Add search value
		if (this.searchValue) {
			params.q = this.searchValue;
		} else if (params.hasOwnProperty('search')) {
			delete params.q;
		}

		// Convert object properties to query string and compose it with url
		const newUrl = Api.setQueryParams(this.urlOrigin, params, { overwrite: true });

		if (this.url === newUrl) {
			return;
		}

		this.url = newUrl;
		this.loadMoreModule.moreUrl = this.url;
	}

	fetchPosts(options = {}) {
		const beforeFetch = options.beforeFetch;
		const afterFetch = options.afterFetch;

		if (beforeFetch && typeof beforeFetch !== 'function') {
			throw new Error('PostFilterService: before fetch posts callback must be a function');
		}

		if (afterFetch && typeof afterFetch !== 'function') {
			throw new Error('PostFilterService: after fetch posts callback must be a function');
		}

		if (beforeFetch) {
			beforeFetch();
		}

		Api.get(this.url)
			.then((res) => {
				const data = res.trim();

				if (afterFetch) {
					afterFetch(data);
				}
			});
	}
}

export class PostFilterPrototype {
	constructor(postFilterService, onResizeInit = false) {
		this.$wrapper = null;
		this.$root = null;
		this.$content = null;
		this.$notFound = null;
		this.$search = null;
		this.$searchInput = null;
		this.$filters = null;
		this.$sortFilterTrigger = null;
		this.$sortFilterTriggerText = null;
		this.$sortFilterMenu = null;

		this.onResizeInit = onResizeInit;
		this.selectList = [];
		this.postFilterService = postFilterService;

		this.setActiveSubthemeFilter = this.setActiveSubthemeFilter.bind(this);
	}

	init() {
		this.$content = document.querySelector('[data-post-filter="content"]');
		this.$notFound = document.querySelector('[data-post-filter="not-found"]')

		if (!this.$content) {
			throw new Error('PostFilter: Content container is missing');
		}

		const $items = this.$content.querySelectorAll('[data-observer-elem="item"]');
		this.$search = this.$root.querySelector('.widget-search-field');

		if (this.$search) {
			this.$searchInput = this.$search.querySelector('.widget-search-field_input');
		}

		this.$filters = this.$root.querySelectorAll('[data-name][data-value]');
		this.$sortFilterTrigger = this.$root.querySelector('[data-dropdown-trigger="postFilterSort"]');

		if (this.$sortFilterTrigger) {
			this.$sortFilterTriggerText = this.$sortFilterTrigger.querySelector('[data-dropdown-trigger-text]');
			this.$sortFilterMenu = this.$root.querySelector('[data-dropdown-menu="postFilterSort"]');

			// Init sort filter dropdown
			this.selectList.push(new Dropdown(this.$sortFilterMenu, this.$sortFilterTrigger, {
				subscriptions: [
					{
						event: 'beforeShow',
						callback: () => {
							// Hide all opened select before to open current
							this.hideAllSelects();
						}
					}
				]
			}));
		}

		if ($items.length) {
			showItems($items);
		}

		if (!this.onResizeInit) {
			this.setInitialValues();
		}

		this.updateFilterElements();
		this.addEventListeners();
	}

	setInitialValues() {
		if (this.$searchInput && this.$searchInput.value.length) {
			this.postFilterService.searchValue = this.$searchInput.value;
		}

		this.$filters.forEach(($filter) => {
			// Set initial filters
			if ($filter.classList.contains('-active')) {
				this.postFilterService.updateFilters({
					name: $filter.dataset.name,
					value: $filter.dataset.value
				})
			}
		});
	}

	/**
	 * Updates filter elements styles if initial state was defined,
	 * or the window has been resized
	 */
	updateFilterElements() {
		if (this.postFilterService.searchValue) {
			this.$searchInput.value = this.postFilterService.searchValue;
		}

		if (this.postFilterService.filters.length) {

			this.postFilterService.filters.forEach((filter) => {
				const $filter = Array.from(this.$filters).find(($filter) =>
					$filter.dataset.name === filter.name && $filter.dataset.value === filter.value);

				// Define filter options
				let filterOptions = this.getFilterOptions($filter, {
					shouldUpdateUrl: false
				});

				// Update filters
				this.setFilter($filter, filterOptions);
			});
		}
	}

	addEventListeners() {
		if (this.$searchInput) {
			/* Search field input event listener */
			this.$searchInput.addEventListener('input', () => {
				const value = this.$searchInput.value.trim();

				if (!value.length) {
					this.postFilterService.searchValue = null;
					this.$search.classList.remove('-filled');
					return;
				}

				this.postFilterService.searchValue = value;
				this.$search.classList.add('-filled');
			});

			/* Search field keyup event listener */
			this.$searchInput.addEventListener('keyup', (e) => {
				if (
					e.key !== 'Enter' ||
					!this.postFilterService.searchValue ||
					this.postFilterService.getFilterValue('search') === this.searchValue
				) {
					return;
				}

				this.postFilterService.refreshUrl();

				/* Fetch posts */
				this.postFilterService.fetchPosts({
					beforeFetch: () => {
						this.toggleNotFoundBlock('hide');
						this.$content.classList.add('-pending');
					},
					afterFetch: (data) => {
						this.updateContent(data);
						this.$content.classList.remove('-pending');
					}
				});
			});
		}

		this.$filters.forEach(($filter) => {
			$filter.addEventListener('click', () => {
				// Check filter element has required data attributes
				if (!$filter.dataset || !$filter.dataset.name || !$filter.dataset.value) {
					throw new Error('PostFilter: Filter element must have name and value data attributes');
				}

				// Define filter options
				const filterOptions = this.getFilterOptions($filter);

				// Update filters
				this.setFilter($filter, filterOptions);
			});
		});
	}

	getFilterOptions($target, options = {}) {
		const name = $target.dataset.name;
		const value = $target.dataset.value;
		const shouldUpdateUrl = options.shouldUpdateUrl === false ? options.shouldUpdateUrl : true;
		const filterOptions = {};

		// Should update fetching posts request url depending on current filter params
		filterOptions.shouldUpdateUrl = shouldUpdateUrl;

		// If target is a theme filter, drop subtheme filter,
		// and set callback that add and remove event listeners
		// to the subtheme filters
		if (name === 'theme') {
			if (shouldUpdateUrl) {
				filterOptions.dropByName = 'subtheme';
			}

			filterOptions.callback = () => {
				const $currentActiveSubthemeList = this.$root.querySelector(`[data-post-filter="subtheme-list"].-active`);
				const $subthemeList = this.$root.querySelector(`[data-post-filter="subtheme-list"][data-id="${value}"]`);

				// Unset active class of current subtheme list,
				// and remove event listeners of its subtheme filters
				if ($currentActiveSubthemeList) {
					const $subthemeFilters = $currentActiveSubthemeList.querySelectorAll('[data-name="subtheme"]');

					$subthemeFilters.forEach(($subthemeFilter) => {
						$subthemeFilter.removeEventListener('click', this.setActiveSubthemeFilter);
						$subthemeFilter.classList.remove('-active');
					});
					$currentActiveSubthemeList.classList.remove('-active');
				}

				// Set active class of subtheme list,
				// and add event listeners of its subtheme filters
				if ($subthemeList) {
					const $subthemeFilters = $subthemeList.querySelectorAll('[data-name="subtheme"]');

					$subthemeFilters.forEach(($subthemeFilter) => {
						$subthemeFilter.addEventListener('click', this.setActiveSubthemeFilter);
					});
					$subthemeList.classList.add('-active');
				}
			}
		} else if (name === 'sort') {
			// Update select button text with active option text and hide dropdowns
			filterOptions.callback = () => {
				this.$sortFilterTriggerText.textContent = $target.textContent;
				this.hideAllSelects();
			}
		}

		return filterOptions;
	}

	setFilter($target, options = {}) {
		const name = $target.dataset.name;
		const value = $target.dataset.value;

		const $oldActive = this.$root.querySelector(`[data-name="${name}"].-active`);

		// If there is currently active filter element,
		// and it's not a target, remove its active class
		if ($oldActive) {
			if ($oldActive.isEqualNode($target) && !options.initial) {
				return;
			}

			$oldActive.classList.remove('-active');
		}

		const filterUpdateOptions = {};

		if (options.dropByName && typeof options.dropByName === 'string') {
			filterUpdateOptions.dropByName = options.dropByName;
		}

		if (options.shouldUpdateUrl) {
			filterUpdateOptions.shouldUpdateUrl = options.shouldUpdateUrl;
		}

		$target.classList.add('-active');

		/* Update filters */
		this.postFilterService.updateFilters({ name, value }, filterUpdateOptions);

		/* Fetch posts */
		this.postFilterService.fetchPosts({
			beforeFetch: () => {
				this.toggleNotFoundBlock('hide');
				this.$content.classList.add('-pending');
			},
			afterFetch: (data) => {
				this.updateContent(data);
				this.$content.classList.remove('-pending');
			}
		});

		if (options.callback && typeof options.callback === 'function') {
			options.callback($target);
		}
	}

	setActiveSubthemeFilter(e) {
		this.setFilter(e.target);
	}

	hideAllSelects() {
		this.selectList.forEach((select) => {
			if (select instanceof Dropdown && select.visible) {
				select.hide();
			}
		});
	}

	updateContent(data) {
		let $items;

		if (!this.$content.innerHTML.trim().length) {
			// If before update there was empty content, reinitialize load more observer
			if (data) {
				this.$content.innerHTML = data;
				$items = this.$content.querySelectorAll('[data-observer-elem="item"]');

				if ($items.length >= 9) {
					this.postFilterService.loadMoreModule.reinit();
				} else {
					this.removeSpinner();
				}
			}
		} else if (!data) {
			// If empty data incoming, remove load more trigger
			this.$content.innerHTML = '';
			this.removeSpinner();
		} else {
			// Render data if it's not empty
			this.$content.innerHTML = data;
			$items = this.$content.querySelectorAll('[data-observer-elem="item"]');

			// If there is no loadMore trigger, reinitialize module
			if (!document.querySelector('[data-observer-elem="trigger"]') && $items.length >= 9) {
				this.postFilterService.loadMoreModule.reinit();
			} else if ($items.length < 9) {
				this.removeSpinner();
			}
		}

		if (!$items) {
			$items = this.$content.querySelectorAll('[data-observer-elem="item"]');
		}

		if ($items.length) {
			showItems($items);
		} else {
			this.$notFound.classList.remove('-hidden');
			this.toggleNotFoundBlock('show');
		}

		lozadObserver.observe();
	}

	removeSpinner() {
		const $spinner = document.querySelector('[data-observer-elem="trigger"]');

		if ($spinner) {
			$spinner.remove();
		}
	}

	toggleNotFoundBlock(state = 'show') {
		if (!this.$notFound) return;

		if (state === 'show') {
			this.$notFound.classList.remove('-hidden');
		} else {
			this.$notFound.classList.add('-hidden');
		}
	}
}

export class DesktopPostFilter extends PostFilterPrototype {
	constructor(postFilterService, onResizeInit) {
		super(postFilterService, onResizeInit);
		this.$root = document.getElementById('postFilterDesktop');

		if (this.$root) {
			this.init();
		}
	}
}

export class MobilePostFilter extends PostFilterPrototype {
	constructor(postFilterService, onResizeInit) {
		super(postFilterService, onResizeInit);
		this.$root = document.getElementById('mobilePostFilter');
		this.$mainFilterTrigger = null;
		this.$mainFilterTriggerText = null;
		this.$mainFilterMenu = null;
		this.$collapsible = null;

		this.collapsible = null;

		if (this.$root) {
			this.init();
		}
	}

	init() {
		this.$mainFilterTrigger = this.$root.querySelector('[data-dropdown-trigger="postFilterMain"]');
		this.$mainFilterTriggerText = this.$mainFilterTrigger.querySelector('[data-dropdown-trigger-text]');
		this.$mainFilterMenu = this.$root.querySelector('[data-dropdown-menu="postFilterMain"]');
		this.$collapsible = this.$root.querySelector('[data-collapsible-root="postFilterThemes"]');

		// Init main filter dropdown
		this.selectList.push(new Dropdown(this.$mainFilterMenu, this.$mainFilterTrigger, {
			subscriptions: [
				{
					event: 'beforeShow',
					callback: () => {
						// Hide all opened select before to open current
						this.hideAllSelects();
					}
				}
			]
		}));

		// Init theme filters collapsible menu
		this.collapsible = new Collapsible({ $root: this.$collapsible });

		super.init();
	}

	getFilterOptions($target) {
		const $filterOptions = super.getFilterOptions($target);

		// Update select button text with active option text and hide dropdowns
		if ($target.closest('[data-dropdown-menu="postFilterMain"]')) {
			this.$mainFilterTriggerText.textContent = $target.textContent;
			this.hideAllSelects();
		}

		return $filterOptions;
	}
}

export default class PostFilter {
	constructor() {
		this.media = window.matchMedia('(min-width: 501px)').matches;

		this.defaultUrl = null;
		this.postFilterService = null;

		this.desktopModule = null;
		this.mobileModule = null;

		this.init();
	}

	init() {
		// Set default url
		const $wrapper = document.querySelector('.post-filter');
		if ($wrapper.dataset && $wrapper.dataset.url) {
			this.defaultUrl = $wrapper.dataset.url;
		}

		this.postFilterService = new PostFilterService(this.defaultUrl);

		if (this.media) {
			this.desktopModule = new DesktopPostFilter(this.postFilterService);
		} else {
			this.mobileModule = new MobilePostFilter(this.postFilterService);
		}

		window.addEventListener('resize', throttle(() => {
			this.media = window.matchMedia('(min-width: 501px)').matches;

			if (this.media) {
				if (!this.desktopModule) {
					this.desktopModule = new DesktopPostFilter(this.postFilterService, true);
				} else {
					this.desktopModule.updateFilterElements();
				}
			} else {
				if (!this.mobileModule) {
					this.mobileModule = new MobilePostFilter(this.postFilterService, true);
				} else {
					this.mobileModule.updateFilterElements();
				}
			}
		}, 20));
	}
}